import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Zone, SerializedItemStatus, DeviceStatus } from '../../types';

interface SerializedItemsFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  zoneId: string;
  onZoneChange: (value: string) => void;
  itemStatus: string;
  onItemStatusChange: (value: string) => void;
  deviceStatus: string;
  onDeviceStatusChange: (value: string) => void;
  zones: Zone[];
}

const itemStatusOptions: { value: SerializedItemStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'in_stock', label: 'In Stock' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'allocated', label: 'Allocated' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'installed', label: 'Installed' },
  { value: 'rma', label: 'RMA' },
  { value: 'scrapped', label: 'Scrapped' },
];

const deviceStatusOptions: { value: DeviceStatus | 'all'; label: string; color: string }[] = [
  { value: 'all', label: 'All 5S Statuses', color: '' },
  { value: 'valid', label: 'Valid', color: 'bg-green-500' },
  { value: 'verify', label: 'Verify', color: 'bg-yellow-500' },
  { value: 'test', label: 'Test', color: 'bg-blue-500' },
  { value: 'dev', label: 'Dev', color: 'bg-purple-500' },
  { value: 'dead', label: 'Dead', color: 'bg-red-500' },
];

export function SerializedItemsFilters({
  search,
  onSearchChange,
  zoneId,
  onZoneChange,
  itemStatus,
  onItemStatusChange,
  deviceStatus,
  onDeviceStatusChange,
  zones,
}: SerializedItemsFiltersProps) {
  return (
    <div className="flex flex-wrap gap-4 items-end">
      {/* Search */}
      <div className="flex-1 min-w-[200px]">
        <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
          Search
        </label>
        <Input
          placeholder="Serial, MAC, Device ID..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-9"
        />
      </div>

      {/* Zone Filter */}
      <div className="w-[180px]">
        <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
          Zone
        </label>
        <Select value={zoneId} onValueChange={onZoneChange}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="All Zones" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Zones</SelectItem>
            {zones.map((zone) => (
              <SelectItem key={zone.id} value={zone.id}>
                <div className="flex items-center gap-2">
                  {zone.color && (
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: zone.color }}
                    />
                  )}
                  <span>{zone.code} - {zone.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Item Status Filter */}
      <div className="w-[160px]">
        <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
          Inventory Status
        </label>
        <Select value={itemStatus} onValueChange={onItemStatusChange}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            {itemStatusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Device Status (5S) Filter */}
      <div className="w-[160px]">
        <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
          5S Status
        </label>
        <Select value={deviceStatus} onValueChange={onDeviceStatusChange}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="All 5S Statuses" />
          </SelectTrigger>
          <SelectContent>
            {deviceStatusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  {option.color && (
                    <div className={`w-3 h-3 rounded-full ${option.color}`} />
                  )}
                  <span>{option.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
